import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardEngagement } from '../sql-maps/bivz-engagement-sessions-with-dimensions-sql-map';

export interface EngagementSessionsByDateDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class EngagementSessionsByDateDataLoaderFactory extends Factory<
    DataLoader,
    EngagementSessionsByDateDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'EngagementSessionsByDateDataLoaderFactory';

    getInstance(options: EngagementSessionsByDateDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardEngagement.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                // TODO add context settings. Remember to update chart options build logic in order to support dynamic choice of day_no
                // const maxDayNo =
                //     queryContext.settings[
                //         QueryContextSettingID.ENGAGEMENT_MAX_DAY_NO
                //     ] ?? 30;
                // const selectedDayNoSQL: Partial<ICompiledSQLTokens> = {
                //     where: [`day_no <= ${maxDayNo}`],
                // };

                const {
                    dateTrunc,
                    splitSelect,
                    splitGroupBy,
                    where,
                    // is_in_context_app_version,
                    // unnestDatasourceColumn,
                    join,
                } = BiVizDashboardEngagement.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    options
                    // selectedDayNoSQL
                );

                // const selectFacts = is_in_context_app_version
                //     ? 'ds.date, ds.proper_id, app_version.*,'
                //     : 'ds.*,';

                const query = `
-- DataLoaderId: ${this.instanceId}

SELECT 
  ${splitSelect}${dateTrunc} AS date,
  APPROX_QUANTILES(duration_minutes, 1000)[OFFSET(500)] AS median_sess_length_mins,
  COUNT(session_no)/COUNT(DISTINCT player_id) AS avg_sess_per_user,
FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                )}\` ds
${join}
${where}
GROUP BY ${splitGroupBy}date
ORDER BY ${splitGroupBy}date ASC`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
